import { Button } from '@/components/Button';
import { IconChevronLeft, IconChevronRight } from '@/components/Icons';
import { formatCount } from '@/lib/format';

export interface TablePagerProps {
  /** 1-based. */
  page: number;
  pageSize: number;
  /** Rows across every page, not just this one. */
  total: number;
  onPageChange: (page: number) => void;
  /** Disables the buttons while a fetch is in flight. */
  busy?: boolean;
  /** Row noun for the count, e.g. "day" → "6 days". */
  noun?: string;
}

/**
 * The footer under a paginated report table.
 *
 * Extracted because the same markup was inline in three pages already and this
 * would have been a fourth. The other three still carry their own copies — they
 * are server-paginated and pass their own params through, so switching them over
 * is a separate change, not a drive-by.
 */
export function TablePager({
  page,
  pageSize,
  total,
  onPageChange,
  busy = false,
  noun,
}: TablePagerProps) {
  const totalPages = Math.max(1, Math.ceil(total / pageSize));
  if (total === 0) return null;

  const start = (page - 1) * pageSize + 1;
  const end = Math.min(total, page * pageSize);
  const label = noun
    ? `${formatCount(total)} ${total === 1 ? noun : `${noun}s`}`
    : formatCount(total);

  return (
    <div className="flex flex-wrap items-center justify-between gap-3 border-t border-slate-200 px-4 py-3.5 sm:px-5">
      <p className="text-xs tabular-nums text-slate-500">
        Showing {start}–{end} of {label}
      </p>
      <div className="flex items-center gap-2">
        <Button
          size="sm"
          icon={<IconChevronLeft className="h-3.5 w-3.5" />}
          disabled={page <= 1 || busy}
          onClick={() => onPageChange(Math.max(1, page - 1))}
        >
          Previous
        </Button>
        <span className="px-1 text-xs tabular-nums text-slate-500">
          Page {page} of {totalPages}
        </span>
        <Button
          size="sm"
          disabled={page >= totalPages || busy}
          onClick={() => onPageChange(Math.min(totalPages, page + 1))}
        >
          Next
          <IconChevronRight className="h-3.5 w-3.5" />
        </Button>
      </div>
    </div>
  );
}
